import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Lock, ShieldCheck, ExternalLink, FileText, MapPin, Layers, Target,
  Loader2, ArrowRight, BellRing, BookOpen, Wallet, Landmark, Banknote,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ComplianceHandoffGateway from "../components/ComplianceHandoffGateway";

const TIER_COLOR = {
  STANDARD:      "bg-slate-100 text-slate-700 border-slate-300",
  INSTITUTIONAL: "bg-blue-100 text-blue-900 border-blue-300",
  HNW:           "bg-amber-100 text-amber-900 border-amber-300",
  SOVEREIGN:     "bg-purple-100 text-purple-900 border-purple-300",
};

// Deal Room hub links — the module-gated destinations that left the sidebar in
// STARSEED-CAPMARKETS-052, plus the FINBPM funding page (STARSEED-CAPMARKETS-063:
// wire instructions are FinBPM-issued; /partner-funding is the only surface that
// mirrors them, so the Deal Room links straight to it). Same gating contract as
// navConfig.visibleNavItems: staff always see a module link; members lose it only
// on explicit module_access=false. `roles`-restricted links never show outside
// those roles (the route itself is member-only).
const HUB_LINKS = [
  { href: "/partner-funding", label: "FINBPM — Wire Instructions", desc: "Official funding instructions · SSB", icon: Banknote, roles: ["client", "referrer"] },
  { href: "/how-to-invest", label: "How to Invest", desc: "Funding steps, qualification, flow", icon: BookOpen, module: "how_to_invest" },
  { href: "/funds", label: "Fund Tracking", desc: "Partner Account · FINBPM", icon: Wallet, module: "fund_tracking" },
  { href: "/partner-account", label: "Partner Account", desc: "FINBPM portal", icon: Landmark, module: "monaxa" },
];

const DealRoomHub = ({ user }) => {
  const privileged = user?.role === "admin" || user?.role === "superuser";
  const modAccess = user?.module_access || {};
  const links = HUB_LINKS.filter((l) => {
    if (l.roles) return l.roles.includes(user?.role);
    return privileged || modAccess[l.module] !== false;
  });
  if (links.length === 0) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="dealroom-hub">
      {links.map(({ href, label, desc, icon: Icon }) => (
        <Link key={href} to={href} data-testid={`dealroom-hub${href.replace(/\//g, "-")}`}>
          <Card className="border-gold/20 bg-obsidian/50 hover:border-gold/40 transition-colors h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <Icon className="w-5 h-5 text-gold flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-cream text-sm font-medium">{label}</p>
                <p className="text-cream/55 text-xs truncate">{desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-cream/40 ml-auto flex-shrink-0" />
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
};

const DealFlowPage = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDeal, setOpenDeal] = useState(null); // current modal-open deal
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api.get("/dealflow/status");
      setStatus(s.data);
      if (s.data.asset_manager_funded) {
        const d = await api.get("/dealflow/deals");
        setDeals(d.data.deals || []);
      }
    } catch (e) {
      toast.error("Failed to load Deal Room");
    } finally {
      setLoading(false);
    }
  };

  const toggleAlerts = async (enabled) => {
    setSavingPref(true);
    try {
      await api.post("/dealflow/preferences/alerts", { enabled });
      setStatus((s) => ({ ...s, deal_alerts_enabled: enabled }));
      toast.success(enabled ? "Deal alerts enabled" : "Deal alerts disabled");
    } catch {
      toast.error("Failed to update preference");
    } finally {
      setSavingPref(false);
    }
  };

  const acknowledge = async () => {
    if (!openDeal) return;
    try {
      // Per-deal acknowledgment (specific to this deal's disclosure text)
      await api.post("/dealflow/acknowledge", { deal_id: openDeal.id });
      // Open the partner deck in a new tab
      if (openDeal.pitch_deck_url) {
        window.open(openDeal.pitch_deck_url, "_blank", "noopener,noreferrer");
      }
      // Refresh list to flip acknowledged flag
      const d = await api.get("/dealflow/deals");
      setDeals(d.data.deals || []);
      setOpenDeal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to acknowledge");
    }
  };

  // Locked-gate view
  if (!loading && status && !status.asset_manager_funded) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto px-4 py-10" data-testid="dealflow-locked">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gold/40 bg-obsidian mb-5">
              <Lock className="w-7 h-7 text-gold" />
            </div>
            <p className="text-gold text-xs tracking-[0.35em] uppercase mb-3">
              Deal Room · Restricted
            </p>
            <h1 className="text-3xl sm:text-4xl font-serif text-cream mb-4">
              Awaiting Partner Funding Confirmation
            </h1>
            <p className="text-cream/70 max-w-xl mx-auto leading-relaxed">
              {status.gate_message}
            </p>
          </div>

          <Card className="border-gold/20 bg-obsidian/60">
            <CardContent className="p-8 space-y-4 text-cream/80 text-sm leading-relaxed">
              <p>
                Access to specific opportunities is granted only after our
                regulated asset-management partner confirms your funded status.
                This is a structural compliance gate — not a discretionary one.
              </p>
              <p className="text-cream/60">
                Your current visibility tier:{" "}
                <Badge className={`border ${TIER_COLOR[status.user_tier] || ""}`}>
                  {status.user_tier_label}
                </Badge>
              </p>
              <div className="pt-3 border-t border-gold/15 flex flex-wrap gap-3">
                <Link to="/command-center">
                  <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="dealflow-back-cc">
                    Return to Command Center
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                {(user?.role === "admin" || user?.role === "superuser" ||
                  user?.module_access?.how_to_invest !== false) && (
                  <Link to="/how-to-invest">
                    <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="dealflow-locked-hti">
                      <BookOpen className="mr-2 w-4 h-4" />
                      How to Invest
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-cream/40 text-[10px] tracking-widest uppercase mt-10">
            EchoSense Capital Club acts solely as an Introducer. No investment advice is provided.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="dealflow-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-gold text-xs tracking-[0.35em] uppercase mb-2">
              Deal Room
            </p>
            <h1 className="text-3xl sm:text-4xl font-serif text-cream">
              Curated Opportunities
            </h1>
            <p className="text-cream/60 mt-2 text-sm">
              Each opportunity below is gated by your visibility tier and
              requires an acknowledged compliance notice prior to disclosure.
            </p>
          </div>
          {status && (
            <div className="flex items-center gap-3" data-testid="dealflow-user-tier">
              <ShieldCheck className="w-5 h-5 text-gold" />
              <div>
                <p className="text-[10px] tracking-widest uppercase text-cream/40">Your Tier</p>
                <Badge className={`border ${TIER_COLOR[status.user_tier] || ""}`}>
                  {status.user_tier_label}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Deal Room hub — the sidebar destinations de-listed by the 2026-07-20
            reorg live here now (STARSEED-CAPMARKETS-052); the embedded FINBPM
            experience is tracked by STARSEED-CAPMARKETS-058. */}
        {!loading && <DealRoomHub user={user} />}

        {loading && (
          <div className="flex items-center justify-center py-20 text-cream/60">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading deals…
          </div>
        )}

        {!loading && status && (
          <Card className="border-gold/20 bg-obsidian/50 mb-6" data-testid="dealflow-alerts-pref">
            <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <BellRing className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-cream text-sm font-medium">
                    Email me when new opportunities are published
                  </p>
                  <p className="text-cream/55 text-xs mt-0.5 leading-relaxed">
                    You'll only receive emails for deals matching your tier.
                    Unlock + compliance notices are always sent.
                  </p>
                </div>
              </div>
              <Switch
                checked={status.deal_alerts_enabled !== false}
                onCheckedChange={toggleAlerts}
                disabled={savingPref}
                data-testid="dealflow-alerts-toggle"
              />
            </CardContent>
          </Card>
        )}

        {!loading && deals.length === 0 && (
          <Card className="border-gold/20 bg-obsidian/60" data-testid="dealflow-empty">
            <CardContent className="p-12 text-center">
              <FileText className="w-10 h-10 text-cream/30 mx-auto mb-4" />
              <h3 className="text-cream text-lg font-medium mb-2">
                No opportunities at your tier right now
              </h3>
              <p className="text-cream/60 text-sm max-w-md mx-auto">
                Deals are added and curated by the EchoSense desk. You will be
                notified when an opportunity matching your profile is published.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && deals.length > 0 && (
          <div className="grid md:grid-cols-2 gap-5" data-testid="dealflow-list">
            {deals.map((d) => (
              <Card
                key={d.id}
                className="border-gold/20 bg-obsidian/70 hover:border-gold/40 transition-colors"
                data-testid={`deal-card-${d.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-cream text-lg font-medium leading-snug">{d.title}</h3>
                    <Badge className={`border whitespace-nowrap ${TIER_COLOR[d.min_tier] || ""}`}>
                      {d.min_tier_label}
                    </Badge>
                  </div>
                  <p className="text-cream/70 text-sm leading-relaxed mb-5">{d.summary}</p>

                  <div className="grid grid-cols-3 gap-3 mb-5 text-xs">
                    {d.sector && (
                      <div className="flex items-center gap-1.5 text-cream/55">
                        <Layers className="w-3.5 h-3.5" /> {d.sector}
                      </div>
                    )}
                    {d.geography && (
                      <div className="flex items-center gap-1.5 text-cream/55">
                        <MapPin className="w-3.5 h-3.5" /> {d.geography}
                      </div>
                    )}
                    {d.target_raise && (
                      <div className="flex items-center gap-1.5 text-cream/55">
                        <Target className="w-3.5 h-3.5" /> {d.target_raise}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => setOpenDeal(d)}
                    className="w-full bg-gold hover:bg-gold/90 text-navy-dark font-medium tracking-widest uppercase text-xs"
                    data-testid={`deal-open-${d.id}`}
                  >
                    {d.acknowledged ? "Re-Open Deck" : "Review & Acknowledge"}
                    <ExternalLink className="ml-2 w-3.5 h-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-center text-cream/40 text-[10px] tracking-widest uppercase mt-16">
          EchoSense Capital Club acts solely as an Introducer. No investment advice is provided.
        </p>
      </div>

      {/* Arms-Length Compliance Handoff Gateway —
          records audit consent before opening any partner deck */}
      <ComplianceHandoffGateway
        open={!!openDeal}
        onClose={() => setOpenDeal(null)}
        onProceed={acknowledge}
        gateway="deal_room"
        context={openDeal?.title}
      />
    </DashboardLayout>
  );
};

export default DealFlowPage;
